/**
 * appLinks.js — where each surface on the pairing screen actually sends you.
 *
 * The pairing screen names three apps that can redeem a code, which raises the obvious question the screen
 * used to leave unanswered: "I don't have that one — where do I get it?" These are the answers.
 *
 * TWO RULES SHAPE THIS FILE:
 *
 * 1. NEVER LINK TO A LISTING THAT ISN'T LIVE. Every URL below was verified to resolve before being added
 *    (Play 200, and the iOS one via the iTunes lookup API). The browser extension is deliberately NOT
 *    pointed at a store: it has no published listing yet — `STORE_LINKS` in extension.js still carries empty
 *    urls — so it goes to our own /extension page, which already knows how to say "coming soon" per browser.
 *    A dead store link on the one screen a stuck user reaches is worse than no link at all.
 *
 * 2. DON'T MAKE PEOPLE CHOOSE WHAT WE CAN INFER. Slap My Hand ships on both platforms, but a phone already
 *    knows which one it is. Only a desktop browser genuinely can't tell — and that is also the case where
 *    neither link is immediately useful, since you cannot install a phone app on a laptop. So mobile gets a
 *    direct link and desktop gets both, labelled.
 */

/** Keep Me Out — Android only; there is no iOS build. */
export const KMO_PLAY_URL = 'https://play.google.com/store/apps/details?id=vikesh.dass.lockmeout';

/** Slap My Hand — cross-platform, so two listings. */
export const SMH_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.kiaanconsulting.slapmyhand';
export const SMH_IOS_URL = 'https://apps.apple.com/app/slapmyhand-pause-apps-live/id6759518742';

/** Our own page, not a store: the extension has no published listing yet. */
export const EXTENSION_PAGE = '/extension';

/**
 * Which mobile platform is this, if any.
 *
 * iPadOS deliberately reports a Mac user-agent, so the `maxTouchPoints` check is what stops an iPad being
 * treated as a desktop and shown a chooser it doesn't need.
 */
export function detectPlatform(nav = typeof navigator !== 'undefined' ? navigator : null) {
  if (!nav) return 'desktop';
  const ua = nav.userAgent || '';
  if (/Android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/i.test(ua)) return 'ios';
  if (/Macintosh/.test(ua) && (nav.maxTouchPoints || 0) > 1) return 'ios'; // iPadOS masquerading as macOS
  return 'desktop';
}

/**
 * Resolve one tile to what it should do when tapped.
 *
 * Returns either `{ kind: 'link', href, external }` — go straight there — or `{ kind: 'choice', options }`
 * when the platform is genuinely ambiguous. Callers render the two shapes differently; nothing here decides
 * how it looks.
 */
export function installTarget(appId, platform = detectPlatform()) {
  switch (appId) {
    case 'keepmeout':
      // Android-only, so the platform never changes the answer.
      return { kind: 'link', href: KMO_PLAY_URL, external: true };

    case 'slapmyhand':
      if (platform === 'android') return { kind: 'link', href: SMH_PLAY_URL, external: true };
      if (platform === 'ios') return { kind: 'link', href: SMH_IOS_URL, external: true };
      return {
        kind: 'choice',
        options: [
          { label: 'App Store', href: SMH_IOS_URL },
          { label: 'Google Play', href: SMH_PLAY_URL },
        ],
      };

    case 'extension':
      // Internal route, not a store — see rule 1 above.
      return { kind: 'link', href: EXTENSION_PAGE, external: false };

    default:
      return null;
  }
}
